/**
 * +world-resources: pure view-model of the in-run loot toasts (DOM-free, unit-tested).
 *
 * World pickups (sim `pickup` events) become WoW-style toasts: icon, name, "+N", rarity colour.
 * Repeats of the same resource merge into the live toast ("+1 Fangsteel" → "+3 Fangsteel") and
 * refresh its life, so a burst of piles reads as one gain. Also the display data for a
 * WorldResourceKind at a key level ('scale' is the key's Coil Scale grade, 'scaleUp' one above),
 * shared by the currencies-row "found this run" counter.
 */
import type { PickupRarity, WorldFinds, WorldResourceKind } from '@keystone/sim';
import { PICKUP_RARITY } from '@keystone/sim';
import { SCALE_GRADES, type ScaleGrade, gradeForKey } from '@keystone/progression';

/** Toast rows on screen at once (oldest drops first). */
export const TOAST_MAX = 5;
/** Base life of a toast; rare+ linger a little longer. */
export const TOAST_LIFE_MS = 3600;
export const TOAST_LIFE_BONUS_MS: Readonly<Record<PickupRarity, number>> = { common: 0, uncommon: 0, rare: 500, epic: 1000, legendary: 1600 };

/** Rarity palette (meta/ui/rarity.ts values, kept local so the HUD has no meta-UI CSS dependency). */
export const LOOT_RARITY_COLOR: Readonly<Record<PickupRarity, string>> = {
  common: '#c9ccd3',
  uncommon: '#3ddc5a',
  rare: '#3b8cff',
  epic: '#b24cf0',
  legendary: '#ff8a1c',
};

const SCALE_NAME: Readonly<Record<ScaleGrade, string>> = { ashen: 'Ashen', jade: 'Jade', gilded: 'Gilded', eternal: 'Eternal' };

export interface LootDisplay {
  /** Merge key (a resource at a grade). */
  key: string;
  /** Plural / singular names ("Jade Scales" / "Jade Scale"). */
  label: string;
  one: string;
  /** ICON_MANIFEST id (`forge.*`), or null for gold (drawn as a glyph). */
  icon: string | null;
  rarity: PickupRarity;
}

/** How a world resource shows at a key level. */
export function lootDisplay(resource: WorldResourceKind, level: number, rarity: PickupRarity = PICKUP_RARITY[resource]): LootDisplay {
  switch (resource) {
    case 'gold':
      return { key: 'gold', label: 'Gold', one: 'Gold', icon: null, rarity };
    case 'fangsteel':
      return { key: 'fangsteel', label: 'Fangsteel', one: 'Fangsteel', icon: 'forge.fangsteel', rarity };
    case 'venom':
      return { key: 'venom', label: 'Viper Venom', one: 'Viper Venom', icon: 'forge.venom', rarity };
    case 'moltskin':
      return { key: 'moltskin', label: 'Moltskin', one: 'Moltskin', icon: 'forge.moltskin', rarity };
    case 'eyeFragment':
      return { key: 'eyeFragment', label: 'Eye Fragments', one: 'Eye Fragment', icon: 'forge.eye_fragment', rarity };
    case 'xp': // +field: XP wisps / bonus XP
      return { key: 'xp', label: 'XP', one: 'XP', icon: null, rarity };
    case 'item': // +field: a gear drop (progression rolls the item)
      return { key: 'item', label: 'Gear', one: 'Gear', icon: null, rarity };
    case 'scale':
    case 'scaleUp': {
      const g = scaleGradeOf(resource, level);
      return { key: `scale.${g}`, label: `${SCALE_NAME[g]} Scales`, one: `${SCALE_NAME[g]} Scale`, icon: `forge.scale.${g}`, rarity };
    }
  }
}

/** The Coil Scale grade a 'scale' / 'scaleUp' pickup is at this key level (progression worldFindsToBundle rule). */
export function scaleGradeOf(resource: 'scale' | 'scaleUp', level: number): ScaleGrade {
  const g = gradeForKey(level);
  if (resource === 'scale') return g;
  return SCALE_GRADES[Math.min(SCALE_GRADES.length - 1, SCALE_GRADES.indexOf(g) + 1)]!;
}

/** "+3 Fangsteel" / "+1 Eye Fragment". */
export function toastText(d: Pick<LootDisplay, 'label' | 'one'>, amount: number): string {
  return `+${amount} ${amount === 1 ? d.one : d.label}`;
}

/** World finds as display rows (largest rarity first, then amount): the "found this run" tooltip. */
export function findsRows(finds: Readonly<WorldFinds> | null | undefined, level: number): { d: LootDisplay; n: number }[] {
  if (!finds) return [];
  const RANK: Readonly<Record<PickupRarity, number>> = { legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4 };
  const rows: { d: LootDisplay; n: number }[] = [];
  for (const [k, v] of Object.entries(finds) as [WorldResourceKind, number][]) {
    if (!v || v <= 0) continue;
    const d = lootDisplay(k, level);
    const prev = rows.find((r) => r.d.key === d.key);
    if (prev) prev.n += v;
    else rows.push({ d, n: v });
  }
  return rows.sort((a, b) => RANK[a.d.rarity] - RANK[b.d.rarity] || b.n - a.n);
}

/** Items picked up this run (gold excluded: it is its own counter). */
export function findsCount(finds: Readonly<WorldFinds> | null | undefined): number {
  if (!finds) return 0;
  let n = 0;
  for (const [k, v] of Object.entries(finds)) if (k !== 'gold' && v && v > 0) n += v;
  return n;
}

export interface LootToast {
  key: string;
  label: string;
  one: string;
  icon: string | null;
  rarity: PickupRarity;
  amount: number;
  /** A text-only notice ("Hidden cache discovered") instead of a resource. */
  note: boolean;
  /** First shown, last refreshed, expires. */
  born: number;
  at: number;
  until: number;
  /** Bumped on every merge so the view can pop the row again. */
  version: number;
}

export class LootToastModel {
  /** Oldest first. */
  readonly entries: LootToast[] = [];

  /** A pickup: merges into the live toast of the same resource, else adds one. */
  push(resource: WorldResourceKind, amount: number, level: number, now: number, rarity?: PickupRarity): LootToast {
    const d = lootDisplay(resource, level, rarity);
    const life = TOAST_LIFE_MS + TOAST_LIFE_BONUS_MS[d.rarity];
    const live = this.entries.find((e) => !e.note && e.key === d.key && e.until > now);
    if (live) {
      live.amount += amount;
      live.at = now;
      live.until = now + life;
      live.version++;
      return live;
    }
    const t: LootToast = { key: d.key, label: d.label, one: d.one, icon: d.icon, rarity: d.rarity, amount, note: false, born: now, at: now, until: now + life, version: 0 };
    this.add(t);
    return t;
  }

  /** A one-line notice (no amount). Identical live notices refresh instead of stacking. */
  note(text: string, rarity: PickupRarity, now: number, icon: string | null = null): LootToast {
    const live = this.entries.find((e) => e.note && e.label === text && e.until > now);
    const life = TOAST_LIFE_MS + TOAST_LIFE_BONUS_MS[rarity];
    if (live) {
      live.at = now;
      live.until = now + life;
      live.version++;
      return live;
    }
    const t: LootToast = { key: `note:${text}`, label: text, one: text, icon, rarity, amount: 0, note: true, born: now, at: now, until: now + life, version: 0 };
    this.add(t);
    return t;
  }

  /** Drop expired toasts; true when anything changed. */
  expire(now: number): boolean {
    const n = this.entries.length;
    for (let i = this.entries.length - 1; i >= 0; i--) if (this.entries[i]!.until <= now) this.entries.splice(i, 1);
    return this.entries.length !== n;
  }

  clear(): void {
    this.entries.length = 0;
  }

  private add(t: LootToast): void {
    this.entries.push(t);
    // Over the cap: drop the least recently refreshed toast (a toast that just merged stays).
    while (this.entries.length > TOAST_MAX) {
      let k = 0;
      for (let i = 1; i < this.entries.length; i++) if (this.entries[i]!.at < this.entries[k]!.at) k = i;
      this.entries.splice(k, 1);
    }
  }
}
